import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'

/**
 * Cloudinary Image Composable
 *
 * Handles Cloudinary image URL generation with support for:
 * - Direct publicId parameter
 * - Filename-based search with tag filtering
 *
 * @returns {Object} Cloudinary utilities and reactive state
 */
export function useCloudinary() {
  const route = useRoute()

  // Configuration from environment variables
  const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME
  const defaultFolder = import.meta.env.VITE_CLOUDINARY_DEFAULT_FOLDER || ''
  const defaultQuality = import.meta.env.VITE_CLOUDINARY_DEFAULT_QUALITY || 'auto'
  const defaultFormat = import.meta.env.VITE_CLOUDINARY_DEFAULT_FORMAT || 'auto'

  // Reactive state
  const publicId = ref('')
  const error = ref(null)
  const isSearching = ref(false)
  const searchMode = ref('publicId') // 'publicId', 'filename', or 'session'
  const sessionImages = ref([]) // For sessionId mode - array of images
  const resourceType = ref('image') // 'image' or 'video' from API response

  // Validation regex for publicId
  const publicIdPattern = /^[a-zA-Z0-9-_\/\.]+$/

  /**
   * Validates a publicId to prevent injection attacks
   * @param {string} id - The publicId to validate
   * @returns {boolean} True if valid
   */
  const validatePublicId = (id) => {
    if (!id || typeof id !== 'string') return false
    if (id.length > 500) return false // Prevent extremely long IDs
    return publicIdPattern.test(id)
  }

  /**
   * Generates a Cloudinary URL with transformations
   * @param {string} id - The publicId of the image
   * @param {Object} transformations - Optional transformation parameters
   * @returns {string} Complete Cloudinary URL
   */
  const generateCloudinaryUrl = (id, transformations = {}) => {
    if (!cloudName) {
      console.error('VITE_CLOUDINARY_CLOUD_NAME is not configured')
      return ''
    }

    if (!validatePublicId(id)) {
      console.error('Invalid publicId format:', id)
      return ''
    }

    // Build transformation string
    const transforms = {
      quality: transformations.quality || defaultQuality,
      format: transformations.format || defaultFormat,
      width: transformations.width || undefined,
      height: transformations.height || undefined,
      crop: transformations.crop || undefined,
      gravity: transformations.gravity || undefined
    }

    // Create transformation parts
    const transformParts = []

    if (transforms.quality) transformParts.push(`q_${transforms.quality}`)
    if (transforms.format) transformParts.push(`f_${transforms.format}`)
    if (transforms.width) transformParts.push(`w_${transforms.width}`)
    if (transforms.height) transformParts.push(`h_${transforms.height}`)
    if (transforms.crop) transformParts.push(`c_${transforms.crop}`)
    if (transforms.gravity) transformParts.push(`g_${transforms.gravity}`)

    const transformString = transformParts.length > 0
      ? transformParts.join(',') + '/'
      : ''

    // Handle folder structure
    let fullPath = id

    // If publicId doesn't include a folder and defaultFolder is set, prepend it
    if (defaultFolder && !id.includes('/')) {
      fullPath = `${defaultFolder}/${id}`
    }

    // Generate final URL
    return `https://res.cloudinary.com/${cloudName}/image/upload/${transformString}${fullPath}`
  }

  /**
   * Search for all images in a session using Cloudinary Search API
   * @param {string} sessionId - The session ID to search for
   * @returns {Promise<Array>} Array of image objects
   */
  const searchBySessionId = async (sessionId) => {
    if (!sessionId) {
      throw new Error('Session ID is required')
    }

    isSearching.value = true
    error.value = null

    try {
      const apiUrl = '/api/search-session'
      const response = await fetch(`${apiUrl}?sessionId=${encodeURIComponent(sessionId)}`)

      const contentType = response.headers.get('content-type')

      if (!contentType || !contentType.includes('application/json')) {
        console.warn('API endpoint not available. Session search requires deployment to Vercel.')
        console.warn('For local development, please use ?publicId parameter instead.')
        throw new Error('Session search is only available in production. Use publicId parameter for local development.')
      }

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || errorData.error || 'Session search failed')
      }

      const data = await response.json()

      if (!data.success || !data.images || data.images.length === 0) {
        throw new Error('No images found for this session')
      }

      return data.images

    } catch (err) {
      console.error('Cloudinary session search error:', err)
      throw err
    } finally {
      isSearching.value = false
    }
  }

  /**
   * Search for image by filename using Cloudinary Search API
   * @param {string} filename - The filename to search for
   * @returns {Promise<string>} The publicId of the found image
   */
  const searchByFilename = async (filename) => {
    if (!filename) {
      throw new Error('Filename is required')
    }

    isSearching.value = true
    error.value = null

    try {
      // Call our serverless function
      const apiUrl = '/api/search-image'
      const response = await fetch(`${apiUrl}?filename=${encodeURIComponent(filename)}`)

      // Check if response is JSON
      const contentType = response.headers.get('content-type')

      if (!contentType || !contentType.includes('application/json')) {
        // Development mode: API endpoint not available
        // This happens in local dev since Vercel functions only work in production
        console.warn('API endpoint not available. Filename search requires deployment to Vercel.')
        console.warn('For local development, please use ?publicId parameter instead.')
        throw new Error('Filename search is only available in production. Use publicId parameter for local development.')
      }

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || errorData.error || 'Search failed')
      }

      const data = await response.json()

      if (!data.success || !data.publicId) {
        throw new Error('No image found with the specified filename and tags')
      }

      // Store resource type from API response
      if (data.resourceType) {
        resourceType.value = data.resourceType
        if (import.meta.env.DEV) {
          console.log('[Cloudinary] Resource type from API:', data.resourceType)
        }
      }

      return data.publicId

    } catch (err) {
      console.error('Cloudinary search error:', err)
      throw err
    } finally {
      isSearching.value = false
    }
  }

  // Computed property for the main image URL
  const imageUrl = computed(() => {
    if (!publicId.value) return ''
    return generateCloudinaryUrl(publicId.value)
  })

  // Watch for route changes and handle publicId, filename, or sessionId
  watch(
    () => ({
      publicId: route.query.publicId,
      filename: route.query.filename,
      sessionId: route.query.sessionId
    }),
    async (params) => {
      // Priority: publicId > sessionId > filename
      if (params.publicId) {
        // Direct publicId mode
        searchMode.value = 'publicId'
        sessionImages.value = []
        if (validatePublicId(params.publicId)) {
          publicId.value = params.publicId
          error.value = null
        } else {
          error.value = 'Invalid publicId format'
          publicId.value = ''
        }
      } else if (params.sessionId) {
        // Session gallery mode
        searchMode.value = 'session'
        publicId.value = ''
        try {
          const images = await searchBySessionId(params.sessionId)
          sessionImages.value = images
          error.value = null
        } catch (err) {
          error.value = err.message
          sessionImages.value = []
        }
      } else if (params.filename) {
        // Filename search mode
        searchMode.value = 'filename'
        sessionImages.value = []
        try {
          const foundPublicId = await searchByFilename(params.filename)
          publicId.value = foundPublicId
          error.value = null
        } catch (err) {
          error.value = err.message
          publicId.value = ''
        }
      } else {
        // No parameters
        searchMode.value = 'publicId'
        publicId.value = ''
        sessionImages.value = []
        error.value = null
      }
    },
    { immediate: true }
  )

  return {
    // State
    publicId,
    imageUrl,
    error,
    isSearching,
    searchMode,
    sessionImages,
    resourceType, // Expose resource type for video detection
  }
}
